import { Building2, MapPin, Phone, User, Loader2, AlertCircle } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { useContactDetails } from '../../hooks/useConversations';
import { SystemUnitDto } from '../../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ContactDetailsProps {
  senderId: string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

export function ContactDetails({ senderId, isOpen, onClose }: ContactDetailsProps) {
  const { data, isLoading, error } = useContactDetails(senderId);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Detalhes do Contato</SheetTitle>
          <SheetDescription>
            Informações e unidades vinculadas
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Error state */}
          {!!error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Erro ao carregar detalhes do contato. Dados temporariamente indisponíveis.
              </AlertDescription>
            </Alert>
          )}

          {/* Contact info */}
          {data && (
            <>
              {/* Basic info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Informações Básicas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Nome</div>
                    <div className="text-base">
                      {data.senderName || 'Não informado'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Telefone</div>
                    <div className="text-base flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      {data.senderPhone || data.senderId}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Cache</div>
                    <div className="text-xs text-muted-foreground">
                      Atualizado em{' '}
                      {format(new Date(data.cachedAt), "dd/MM/yyyy 'às' HH:mm", {
                        locale: ptBR,
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* System Units */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Unidades Vinculadas</h3>
                  <Badge variant="secondary">{data.systemUnits.length}</Badge>
                </div>

                {data.systemUnits.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-8">
                    Nenhuma unidade vinculada
                  </div>
                ) : (
                  <div className="space-y-3">
                    {data.systemUnits.map((unit: SystemUnitDto) => (
                      <Card key={unit.id}>
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <Building2 className="h-4 w-4" />
                            {unit.systemUnitName || `Unidade ${unit.systemUnitId}`}
                          </CardTitle>
                          {unit.senderName && (
                            <CardDescription>
                              Nome cadastrado: {unit.senderName}
                            </CardDescription>
                          )}
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {/* Address */}
                          {(unit.endereco.rua || unit.endereco.cidade) && (
                            <div className="flex items-start gap-2 text-sm">
                              <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                              <div className="flex-1">
                                {unit.endereco.rua && (
                                  <div>
                                    {unit.endereco.rua}
                                    {unit.endereco.numero && `, ${unit.endereco.numero}`}
                                  </div>
                                )}
                                {unit.endereco.complemento && (
                                  <div className="text-muted-foreground">
                                    {unit.endereco.complemento}
                                  </div>
                                )}
                                {unit.endereco.bairro && (
                                  <div>
                                    {unit.endereco.bairro}
                                    {unit.endereco.cidade &&
                                      ` - ${unit.endereco.cidade}`}
                                    {unit.endereco.estado &&
                                      `/${unit.endereco.estado}`}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="text-xs text-muted-foreground">
                            ID: {unit.systemUnitId}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
